import React from 'react';

import { Modal, Button, Title, Group, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import DocumentAutoComplete, {
  DocumentAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/DocumentAutoComplete';
import useCreateIssuedDocument from 'hooks/document/useCreateIssuedDocument';
import { AthleteRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

interface FormValues {
  template: DocumentAutoCompleteValue;
}

const WaiverCreateModal = ({
  opened,
  athlete,
  closeHandler,
}: {
  opened: boolean;
  athlete: AthleteRetrieve;
  closeHandler: () => void;
}): JSX.Element => {
  const createMutation = useCreateIssuedDocument();

  const form = useForm<FormValues>({
    initialValues: {
      template: {
        documentId: '',
        value: '',
        label: '',
      },
    },
    validate: {
      template: value => (value.documentId ? null : 'A waiver template is required'),
    },
  });

  const handleCloseAndReset = (): void => {
    form.reset();
    closeHandler();
  };

  const handleTemplateChange = (item: DocumentAutoCompleteValue | null): void => {
    const newValue = {
      value: item?.value || '',
      label: item?.label || '',
      documentId: item?.documentId || '',
    };
    form.setFieldValue('template', newValue);
  };

  const handleSubmit = (values: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    createMutation.mutate(
      {
        athlete: athlete.athleteId,
        template: values.template.documentId,
        athletePackage: null,
      },
      {
        onError: error => {
          errorNotification('Waiver Creation Failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Waiver Created', 'Waiver has been created successfully');
          handleCloseAndReset();
        },
        onSettled: () => {
          handleCloseAndReset();
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCloseAndReset}
      size='auto'
      centered
      title={<Title order={4}>Create Waiver</Title>}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Text color='dimmed' size='sm'>
            A waiver for this athlete will be generated from the template provided.
          </Text>
          <DocumentAutoComplete
            {...form.getInputProps('template')}
            size='xs'
            label='Waiver Template'
            placeholder='Select waiver template'
            type='waiver'
            isTemplate
            value={form.values.template}
            onChange={handleTemplateChange}
          />
          <Group position='right'>
            <Button variant='light' onClick={handleCloseAndReset} size='xs' radius='md'>
              Cancel
            </Button>
            <Button type='submit' size='xs' radius='md' disabled={createMutation.isLoading}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default WaiverCreateModal;
